import { useMemo, useRef, useEffect } from "react";
import SntClientTableViewCard from "@/components/SntTableViewCard/SntClientTableViewCard";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { CellTowerAnalysisItem } from "@/apis/wifiCellularMismatchClient";
import { SuccessTag, PrimaryTag, OrangeTag } from "@cleanup/components/TagMore";
import IcomoonIcon from "@icons/IcomoonIcon";
import GlobalUtils from "@/utils/GlobalUtils";
import SntActionButton from "@/cleanup/components/Buttons/SntActionButton";
import { formatDistance } from "@/utils/distanceUtils";

interface CellTowerAnalysisTableProps {
  data: CellTowerAnalysisItem[];
  onViewTower?: (tower: CellTowerAnalysisItem) => void;
}

const CellTowerAnalysisTable = ({ data, onViewTower }: CellTowerAnalysisTableProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const table = useRef<any>(null);

  const allColumns = useMemo(
    () => [
      {
        key: "actions",
        title: "",
        disableSortBy: true,
        immovable: true,
        canResize: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const tower = cell.row.original;
          // Only show view button if tower has both locations
          if (!tower.harvestedFound || tower.externalLat === null || tower.harvestedLat === null) {
            return null;
          }
          return (
            <SntActionButton
              tooltipText={translate(LanguageKey.view_on_map_key)}
              onClick={() => onViewTower?.(tower)}
            >
              <IcomoonIcon icon="map2" size="16" color="#0071a1" />
            </SntActionButton>
          );
        },
      },
      {
        key: "distanceMeters",
        title: translate(LanguageKey.distance_key),
        disableSortBy: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          return formatDistance(cell.row.original.distanceMeters);
        },
      },
      {
        key: "cellularInfo",
        title: translate(LanguageKey.cellular_tower_info),
        disableSortBy: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const { mcc, mnc, area, cellId } = cell.row.original;
          const info = GlobalUtils.formatCellularTowerInfo(
            { mcc, net: mnc, area, cellId },
            ""
          );
          return <div>{info}</div>;
        },
      },
      {
        key: "externalLocation",
        title: translate(LanguageKey.external_location_key),
        disableSortBy: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const { externalLat, externalLng } = cell.row.original;
          if (externalLat === null || externalLng === null) return "-";
          return (
            <>
              <div className="d-flex">
                {externalLat.toFixed(5)} &nbsp;
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`http://maps.google.com/maps?q=loc:${externalLat},${externalLng}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IcomoonIcon icon="map2" size="12" color="#dc3545" />
                </a>
              </div>
              <div>{externalLng.toFixed(5)}</div>
            </>
          );
        },
      },
      {
        key: "harvestedLocation",
        title: translate(LanguageKey.harvested_location_key),
        disableSortBy: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const { harvestedLat, harvestedLng, harvestedFound } = cell.row.original;
          if (!harvestedFound) {
            return (
              <span style={{ color: "#6c757d", fontStyle: "italic" }}>
                {translate(LanguageKey.no_harvested_data_key)}
              </span>
            );
          }
          return (
            <>
              <div className="d-flex">
                {harvestedLat.toFixed(5)} &nbsp;
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`http://maps.google.com/maps?q=loc:${harvestedLat},${harvestedLng}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IcomoonIcon icon="map2" size="12" color="#28a745" />
                </a>
              </div>
              <div>{harvestedLng.toFixed(5)}</div>
            </>
          );
        },
      },
      {
        key: "observationCount",
        title: translate(LanguageKey.observation_count_key),
        disableSortBy: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const count = cell.row.original.observationCount;
          return count != null ? count.toLocaleString() : "-";
        },
      },
      {
        key: "confidence",
        title: translate(LanguageKey.confidence_key),
        disableSortBy: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const confidence = cell.row.original.confidence;
          if (!confidence) return "-";
          if (confidence === "HIGH") {
            return <SuccessTag tagName={translate(LanguageKey.confidence_high_key)} />;
          } else if (confidence === "MEDIUM") {
            return <PrimaryTag tagName={translate(LanguageKey.confidence_medium_key)} />;
          } else if (confidence === "LOW") {
            return <OrangeTag tagName={translate(LanguageKey.confidence_low_key)} />;
          }
          return confidence;
        },
      },
      {
        key: "occurrenceCount",
        title: translate(LanguageKey.occurrence_count_key),
        disableSortBy: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          const count = cell.row.original.occurrenceCount;
          return count != null ? count.toLocaleString() : "-";
        },
      },
      {
        key: "country",
        title: translate(LanguageKey.country_key),
        disableSortBy: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          return cell.row.original.country || "-";
        },
      },
      {
        key: "network",
        title: translate(LanguageKey.network_key),
        disableSortBy: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cell: ({ cell }: any) => {
          return cell.row.original.network || "-";
        },
      },
    ],
    [onViewTower]
  );

  const defaultColumnInit = useMemo(
    () => [
      "actions",
      "distanceMeters",
      "cellularInfo",
      "externalLocation",
      "harvestedLocation",
      "observationCount",
      "confidence",
      "occurrenceCount",
    ],
    []
  );

  useEffect(() => {
    if (table.current && data) {
      table.current.setData(data);
      table.current.setSortByKey("distanceMeters", "desc");
    }
  }, [data]);

  return (
    <SntClientTableViewCard
      ref={table}
      allColumns={allColumns}
      visibleCols={defaultColumnInit}
      isFloatingFooter={false}
    />
  );
};

export default CellTowerAnalysisTable;
